// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { OperationRequestOptions } from '@azure/core-client';
import { GithubInstallExchangeOptions, HttpHeader, isAbsoluteUrl, isNonEmptyString } from '@microsoft/vscodeedu-api';
import { getRouteUrl, trackInfo, trackWarning } from '../utilities';
import { anonymousClient } from './config-context';
import { Redirecting, VSCodeEduIdentityProvider } from './vsce-identity-provider';

const prefix = 'GithubLinkHandler:';
const redirectRouteKey = `${prefix}redirectRoute`;
const affinityRegionKey = `${prefix}affinityRegion`;

/**
 * Handles GitHub OAuth flow for linking GitHub account.
 */
export class GithubLinkHandler {
    private trace = (message: string) => trackInfo(`${prefix} ${message}`);
    private warning = (message: string) => trackWarning(`${prefix} ${message}`);

    private readonly identityProvider: VSCodeEduIdentityProvider;

    constructor() {
        this.identityProvider = new VSCodeEduIdentityProvider();
    }

    private get redirectRoute(): string | undefined {
        return sessionStorage.getItem(redirectRouteKey) ?? undefined;
    }

    private set redirectRoute(value: string | undefined) {
        if (value !== undefined) {
            this.trace('redirectRoute: Storing redirect route in session storage');
            sessionStorage.setItem(redirectRouteKey, value);
        } else {
            this.trace('redirectRoute: Removing redirect route from session storage');
            sessionStorage.removeItem(redirectRouteKey);
        }
    }

    private get affinityRegion(): string | undefined {
        return sessionStorage.getItem(affinityRegionKey) ?? undefined;
    }

    private set affinityRegion(value: string | undefined) {
        if (value !== undefined) {
            this.trace('affinityRegion: Storing affinity region in session storage');
            sessionStorage.setItem(affinityRegionKey, value);
        } else {
            this.trace('affinityRegion: Removing affinity region from session storage');
            sessionStorage.removeItem(affinityRegionKey);
        }
    }

    public async linkGithubAccount(redirectTo?: string): Promise<never> {
        if (redirectTo === undefined || isAbsoluteUrl(redirectTo)) {
            this.redirectRoute = redirectTo;
        } else {
            this.redirectRoute = new URL(getRouteUrl(redirectTo), window.location.origin).toString();
        }

        this.trace(`startLink: Creating a new GitHub OAuth flow with redirect to ${this.redirectRoute}`);
        this.redirect(await this.startLink());
    }

    // Redirects to specified URL and throws Redirecting error.
    private redirect(url: string, replace?: boolean): never {
        this.trace(`Redirecting to url: ${url} replace: ${replace}`);
        if (replace === true) {
            window.location.replace(url);
        } else {
            window.location.assign(url);
        }
        throw new Redirecting();
    }

    private async startLink() {
        this.trace('startLink: Starting GitHub OAuth flow');
        let result: string;
        const requestOptions = await this.getRequestOptions();

        const root = window.location.origin;
        const redirectUrl = new URL(getRouteUrl('/github-redirect'), root).toString();

        await anonymousClient.githubInstallStart(redirectUrl, {
            onResponse: ({ status, bodyAsText, headers }) => {
                if (status === 200 && isNonEmptyString(bodyAsText)) {
                    result = bodyAsText;
                    this.affinityRegion = headers.get(HttpHeader.XMSAffinityRegion);
                } else {
                    this.warning(`Failed to obtain github install URL: ${status}.`);
                    throw new Error(`Failed to obtain github install URL: ${status}.`);
                }
            },
            requestOptions,
        });
        return result!;
    }

    public async handleGithubLinkRedirect(): Promise<void> {
        if (window.location.pathname !== getRouteUrl('github-redirect')) {
            return;
        }

        this.trace('handleRedirect: Handling GitHub OAuth redirect');

        const params = new URLSearchParams(window.location.search);

        const code = params.get('code');
        if (code === null) {
            this.warning('handleRedirect: Missing code parameter in URL.');
            return;
        }

        const state = params.get('state');
        if (!isNonEmptyString(state)) {
            this.warning("handleEntraRedirect: Missing 'state' query parameter.");
            return;
        }

        this.trace('handleRedirect: Completing GitHub OAuth flow');
        await this.completeGithubLink(code, state);

        const redirectTo = this.redirectRoute;
        if (isNonEmptyString(redirectTo)) {
            this.trace(`handleGithubLinkRedirect: Redirecting to ${redirectTo}`);
            this.redirectRoute = undefined;
            this.affinityRegion = undefined;
            this.redirect(redirectTo, true);
        }
    }

    private async completeGithubLink(code: string, state: string): Promise<void> {
        this.trace('completeGithubLink: Completing GitHub OAuth flow');
        const options: GithubInstallExchangeOptions = { accessCode: code, state };

        const requestOptions = await this.getRequestOptions();
        const affinityRegion = this.affinityRegion;
        if (affinityRegion !== undefined) {
            requestOptions.customHeaders = { [HttpHeader.XMSAffinityRegion]: affinityRegion };
        }

        await anonymousClient.githubInstallExchange(options, { requestOptions });
    }

    private async getRequestOptions(): Promise<OperationRequestOptions> {
        return {
            customHeaders: {
                [HttpHeader.Authorization]: `Bearer ${await this.identityProvider.getAccessToken()}`,
            },
        };
    }
}
